import React, { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { List, Box } from '@mui/material'
import SubMenuNavigation from './SubMenuNavigation'
import { useFeedbackCounts } from '../hooks/useFeedbackCounts'
import {
  CATEGORY_ORDER as PLATFORM_REPORTS_CATEGORY_ORDER,
  categoryToSlug as platformReportsCategoryToSlug,
  getCategoryIcon as getPlatformReportsCategoryIcon,
} from '../pages/admin/PlatformReports/components/reportHelpers'
import { useAuth } from '../contexts/AuthContext'
import { hasFeatureAccess } from '../services/auth'
import { buildAdminNav, type AdminNavLink } from '../config/adminPages'

interface NavigationProps {
  collapsed?: boolean
  railWidth?: number
}

// Shape SubMenuNavigation consumes (icons are resolved to elements here).
type NavSubItem = {
  path: string
  label: string
  icon: React.ReactElement
  external?: boolean
  badge?: number
}

const SHORT_VIEWPORT = '@media (max-height: 800px)'

const Navigation: React.FC<NavigationProps> = ({ collapsed = false, railWidth = 280 }) => {
  const location = useLocation()
  const { user } = useAuth()
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { counts: feedbackCounts } = useFeedbackCounts()

  const currentPath = location.pathname

  // Highlight the parent whose section the current route sits within.
  const isInSection = (basePath: string) => currentPath.startsWith(basePath)

  // Feature gate per nav group - delegates to the shared auth helper, no
  // duplicated RBAC logic. (EVO_ADMIN bypass lives inside hasFeatureAccess.)
  const hasRequiredFeature = (feature?: string) => {
    if (!feature) return true
    return hasFeatureAccess(user, feature)
  }

  // Shared sub-menu hover/flyout state handlers (unchanged behaviour).
  const handleSubMenuActivate = (menuKey: string) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setActiveSubMenu(menuKey)
  }

  const handleSubMenuDeactivate = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveSubMenu(null)
    }, 200)
  }

  const handleSubMenuStayActive = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  // Dynamic Platform Reports category rows (order + icons from reportHelpers).
  const reportCategoryItems: NavSubItem[] = PLATFORM_REPORTS_CATEGORY_ORDER.map((cat) => ({
    path: `/admin/platform-reports?category=${platformReportsCategoryToSlug(cat)}`,
    label: cat,
    icon: getPlatformReportsCategoryIcon(cat),
  }))

  const resolveBadge = (link: AdminNavLink): number | undefined =>
    link.badgeKey === 'feedbackNew' ? feedbackCounts.new_total || undefined : undefined

  const groups = buildAdminNav().filter((group) => hasRequiredFeature(group.featureGate))

  return (
    <Box sx={{ px: collapsed ? 0 : 1 }}>
      <List sx={{ [SHORT_VIEWPORT]: { py: 0.5 } }}>
        {groups.map((group) => {
          const subItems: NavSubItem[] = group.children.map((child) => ({
            path: child.path,
            label: child.label,
            icon: React.createElement(child.icon),
            external: child.external,
            badge: resolveBadge(child),
          }))
          if (group.appendReportCategories) {
            subItems.push(...reportCategoryItems)
          }

          return (
            <SubMenuNavigation
              key={group.basePath}
              label={group.label}
              icon={React.createElement(group.icon)}
              basePath={group.basePath}
              subItems={subItems}
              isActive={activeSubMenu === group.basePath}
              isInCurrentSection={isInSection(group.basePath)}
              onActivate={() => handleSubMenuActivate(group.basePath)}
              onDeactivate={handleSubMenuDeactivate}
              onStayActive={handleSubMenuStayActive}
              collapsed={collapsed}
              railWidth={railWidth}
            />
          )
        })}
      </List>
    </Box>
  )
}

export default Navigation
